// Wire-shape responses for /articles/* endpoints.
import { z } from 'zod';
import { Article, ArticleAccessMode, ArticleBody, ArticleCollaboratorRole, ArticleCollaboratorTargetType, ArticleSummary, ArticleTranslationLocale, ArticleTranslationSourceLocale, ArticleTranslationStatus, ArticleWechatThemeDefinition, } from './article.js';
export const ArticleResponse = z.object({ article: Article });
export const ArticlePermanentDeleteResponse = z.object({
    deleted: z.literal(true),
    id: z.string().uuid(),
});
export const ArticleListResponse = z.object({
    articles: z.array(ArticleSummary),
    total: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
});
export const ArticleCapabilitiesResponse = z.object({
    enabledAccessModes: z.array(ArticleAccessMode),
    collaborationEnabled: z.boolean(),
    wechatExportEnabled: z.boolean(),
    wechatCustomThemesEnabled: z.boolean(),
});
export const ArticleCollaborator = z.object({
    id: z.string().uuid(),
    articleId: z.string().uuid(),
    targetType: ArticleCollaboratorTargetType,
    targetId: z.string().uuid(),
    role: ArticleCollaboratorRole,
    grantedByUserId: z.string().uuid().nullable(),
    targetName: z.string().nullable(),
    targetAvatar: z.string().nullable(),
    disabledAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const ArticleCollaboratorListResponse = z.object({
    collaborators: z.array(ArticleCollaborator),
});
export const ArticleCollaboratorResponse = z.object({
    collaborator: ArticleCollaborator,
});
export const ArticleCollaborationParticipant = z.object({
    userId: z.string().uuid(),
    name: z.string(),
    avatar: z.string().nullable(),
    color: z.string(),
    role: ArticleCollaboratorRole.nullable(),
    connectedAt: z.string().datetime({ offset: true }),
});
export const ArticleCollaborationSession = ArticleCollaborationParticipant.extend({
    sessionId: z.string().uuid(),
    clientSessionId: z.string().min(1).max(128),
    lastSeenAt: z.string().datetime({ offset: true }),
});
export const ArticleCollaborationStatusResponse = z.object({
    enabled: z.boolean(),
    viewerRole: ArticleCollaboratorRole.nullable(),
    participants: z.array(ArticleCollaborationParticipant),
    sessions: z.array(ArticleCollaborationSession),
});
export const ArticleCollaborationTicketResponse = z.object({
    ticket: z.string(),
    wsUrl: z.string(),
    expiresAt: z.string().datetime({ offset: true }),
});
export const ArticleWechatThemeSummary = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    builtin: z.boolean(),
    ownerId: z.string().uuid().nullable(),
    definition: ArticleWechatThemeDefinition,
    createdAt: z.string().datetime({ offset: true }).nullable(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ArticleWechatThemesResponse = z.object({
    themes: z.array(ArticleWechatThemeSummary),
});
export const ArticleWechatThemeResponse = z.object({
    theme: ArticleWechatThemeSummary,
});
export const ArticleWechatHtmlResponse = z.object({
    html: z.string(),
    title: z.string(),
    themeId: z.string(),
    themeName: z.string(),
    warnings: z.array(z.string()),
});
export const OkResponse = z.object({ ok: z.literal(true) });
export const ArticleTranslation = z.object({
    articleId: z.string().uuid(),
    targetLocale: ArticleTranslationLocale,
    sourceLocale: ArticleTranslationSourceLocale,
    status: ArticleTranslationStatus,
    title: z.string(),
    summary: z.string(),
    body: ArticleBody,
    bodyText: z.string(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ArticleTranslationResponse = z.object({
    translation: ArticleTranslation,
});
// ---------------------------------------------------------------------------
// Reading-layer response shapes.
//
// `prev` / `next` return a single ArticleSummary (or null if the boundary
// of the corpus is reached); `related` returns ranked summaries with the
// shared-tag count surfaced; `breadcrumb` returns a category subset
// projection (root → leaf), trimmed from the full Category schema so the
// reading layer doesn't pull in every taxonomy column.
// ---------------------------------------------------------------------------
export const PrevNextResponse = z.object({
    article: ArticleSummary.nullable(),
});
/** Single related entry — an article summary plus the shared-tag count
 *  the ranker used. `shared` is always ≥ 1 (the route filters out
 *  zero-overlap rows). */
export const RelatedArticle = ArticleSummary.extend({
    shared: z.number().int().min(1),
});
export const RelatedResponse = z.object({
    related: z.array(RelatedArticle),
});
/** Trimmed category projection used by the article breadcrumb route.
 *  The full Category schema lives in `categories/category.ts` (a future
 *  contracts module); this subset is what the article reading layer
 *  actually needs to render the trail. */
export const BreadcrumbCategory = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    parentId: z.string().uuid().nullable(),
});
export const BreadcrumbResponse = z.object({
    /** Root → leaf order. Empty when the article has no category attached
     *  OR every ancestor was archived. */
    breadcrumb: z.array(BreadcrumbCategory),
});
export const ArticleReadCompletion = z.object({
    articleId: z.string().uuid(),
    completed: z.boolean(),
    duplicate: z.boolean(),
    activityId: z.string().uuid().nullable(),
    completedAt: z.string().datetime({ offset: true }),
});
export const ArticleReadCompletionResponse = z.object({
    completion: ArticleReadCompletion,
});
// ---------------------------------------------------------------------------
// Versions wire shapes (Phase 1B-3 — `articles.versions` flag).
// ---------------------------------------------------------------------------
/** Lightweight projection used by the version list endpoint — no
 *  body / bodyText (those land via the detail endpoint to keep list
 *  payloads small and cacheable). */
export const ArticleVersionSummary = z.object({
    id: z.string().uuid(),
    articleId: z.string().uuid(),
    versionNo: z.number().int().positive(),
    snapshotTitle: z.string(),
    snapshotSummary: z.string(),
    snapshotCoverImageUrl: z.string().nullable(),
    snapshotStatus: z.enum(['draft', 'scheduled', 'published', 'unpublished', 'archived']),
    createdAt: z.string().datetime({ offset: true }),
    createdByUserId: z.string().uuid().nullable(),
    reason: z.enum(['edit', 'restore-pre']),
});
/** Full version snapshot used by the detail endpoint — includes the
 *  body snapshot + flattened body_text + SEO. */
export const ArticleVersionDetail = ArticleVersionSummary.extend({
    snapshotBody: ArticleBody,
    snapshotBodyText: z.string(),
    // `ogImageUrl` was dropped from the live SEO bundle in 0021.
    // The schema below stays `optional()` for every key so older
    // snapshots that still carry an `ogImageUrl` key validate
    // through (zod ignores extra keys by default; we just don't
    // surface or restore the value).
    snapshotSeo: z.object({
        seoTitle: z.string().nullable().optional(),
        seoDescription: z.string().nullable().optional(),
        canonicalUrl: z.string().nullable().optional(),
        ogTitle: z.string().nullable().optional(),
        ogDescription: z.string().nullable().optional(),
        noindex: z.boolean().optional(),
        nofollow: z.boolean().optional(),
    }),
});
export const ArticleVersionListResponse = z.object({
    versions: z.array(ArticleVersionSummary),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
});
export const ArticleVersionResponse = z.object({ version: ArticleVersionDetail });
/** Restore returns the post-restore article DTO (so the SDK can
 *  refresh its cache without a follow-up GET) plus the two version
 *  numbers — the one applied + the pre-restore snapshot the service
 *  captured. The pre-restore number drives the SDK's "Undo" button. */
export const ArticleVersionRestoreResponse = z.object({
    article: Article,
    restoredVersionNo: z.number().int().positive(),
    preRestoreVersionNo: z.number().int().positive(),
});
//# sourceMappingURL=responses.js.map